// v2 flag — a swallow-tailed pennant on a leaning pole: the pole rakes gently
// forward from a stone footing, held by a guy rope staked to the ground on the
// opposite side, with a gold finial and a red pennant whose tail splits in two.
// Builds fast, so no scaffolding. Ported 1:1 from age-of-mcp's v2/flag.ts, using
// the library's shared `foundationSlab` helper.
import * as THREE from "three";
import { defineModel, foundationSlab, type Part } from "../../lib";

const POLE = "#6b4a22";
const CLOTH = "#c0392b";
const LEAN = 0.13;           // pole rake (radians, top toward +x)

// The leaning pole of length `len`, its base at the origin.
function pole(len: number): Part {
  return {
    geometry: new THREE.CylinderGeometry(0.022, 0.03, len, 5),
    color: POLE,
    position: [Math.sin(LEAN) * len / 2, Math.cos(LEAN) * len / 2, 0],
    rotation: [0, 0, -LEAN],
  };
}

// A point on the pole's axis at distance `d` from the base.
function onPole(d: number): [number, number] {
  return [Math.sin(LEAN) * d, Math.cos(LEAN) * d];
}

// The stone footing the pole is set in.
function footing(): Part {
  return { geometry: new THREE.BoxGeometry(0.10, 0.06, 0.10), color: "#7e7a74",
    position: [0, 0.03, 0] };
}

// The guy rope from a ground stake at (-0.20, 0) up to the pole at `d`,
// plus the stake itself.
function rigging(d: number): Part[] {
  const [px, py] = onPole(d);
  const sx = -0.20, sy = 0.03;
  const vx = px - sx, vy = py - sy;
  const len = Math.hypot(vx, vy);
  // A cylinder's +y axis rotated by [0, 0, theta] points (-sin theta, cos theta).
  const theta = -Math.asin(vx / len);
  return [
    { geometry: new THREE.CylinderGeometry(0.005, 0.005, len, 3), color: "#cbb389",
      position: [(px + sx) / 2, (py + sy) / 2, 0], rotation: [0, 0, theta] },
    { geometry: new THREE.BoxGeometry(0.03, 0.07, 0.03), color: "#55422a",
      position: [sx, 0.035, 0], rotation: [0, 0, 0.15] },
  ];
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.14, 0.04)];
  if (s === 1) return [footing(), pole(0.3)];
  if (s === 2) return [footing(), pole(0.55), ...rigging(0.45)];

  const [tx, ty] = onPole(0.8);
  return [
    footing(),
    pole(0.8),
    ...rigging(0.62),
    // Gold finial at the tip.
    { geometry: new THREE.IcosahedronGeometry(0.025, 0), color: "#d8b94e",
      position: [tx, ty + 0.015, 0] },
    // The swallow-tailed pennant: a main panel and two splitting tails.
    { geometry: new THREE.BoxGeometry(0.20, 0.10, 0.012), color: CLOTH,
      position: [tx + 0.115, ty - 0.065, 0], rotation: [0, 0, -0.06] },
    { geometry: new THREE.BoxGeometry(0.10, 0.04, 0.012), color: CLOTH,
      position: [tx + 0.255, ty - 0.035, 0], rotation: [0, 0, 0.3] },
    { geometry: new THREE.BoxGeometry(0.10, 0.04, 0.012), color: CLOTH,
      position: [tx + 0.255, ty - 0.095, 0], rotation: [0, 0, -0.3] },
  ];
}

export const flagV2 = defineModel({
  id: "flag-v2",
  tiles: { w: 1, h: 1 },
  batchId: "fable",
  name: "Flag (v2)",
  tags: ["building","flag","low-poly","age-of-mcp","fable"],
  summary: "Low-poly flag.",
  description: "A tall pole flying a pennant, marking territory and inscribed with a short name or message.",
  author: "age-of-mcp",
  footprint: 0.1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [],
});
